import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import { goodsActions, goodsSelectors, goodsThunks } from '@crm/features/goods/goodsSlice';
import { hasCrmPermission } from '@crm/permissions';
import { UiButton, UiInput, UiTextarea } from '@shared/common/ui';

function fmtPrice(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('ru-RU').format(v) + ' ₽';
}

export function GoodsPage() {
  const { branchId } = useParams();
  const dispatch = useDispatch();

  const list = useSelector(goodsSelectors.selectList(branchId));
  const listStatus = useSelector(goodsSelectors.selectListStatus(branchId));
  const saveStatus = useSelector(goodsSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(goodsSelectors.selectSaveError(branchId));
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const canCreate = hasCrmPermission(profile, branches, branchId, 'goods.create');
  const canEdit = hasCrmPermission(profile, branches, branchId, 'goods.edit');
  const canDelete = hasCrmPermission(profile, branches, branchId, 'goods.delete');
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (branchId) dispatch(goodsThunks.list({ branchId }));
  }, [dispatch, branchId]);

  return (
    <section>
      <header className="page-header">
        <h1>Склад</h1>
        {canCreate && (
          <UiButton type="button" onClick={() => setEditing({})}>
            Новый товар
          </UiButton>
        )}
      </header>

      {editing !== null && (editing.id ? canEdit : canCreate) && (
        <GoodsForm
          branchId={branchId}
          goods={editing}
          saveStatus={saveStatus}
          saveError={saveError}
          onCancel={() => {
            setEditing(null);
            dispatch(goodsActions.resetSave(branchId));
          }}
          onSaved={() => setEditing(null)}
        />
      )}

      <div className="form-card" style={{ marginTop: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Артикул</th>
              <th>Цена</th>
              <th>Остаток</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((g) => (
              <tr key={g.id}>
                <td>{g.name}</td>
                <td>{g.articleNumber ?? '—'}</td>
                <td>{fmtPrice(g.price ?? g.defaultPrice)}</td>
                <td>{g.goodsCount ?? 0}</td>
                <td style={{ textAlign: 'right' }}>
                  {canEdit && (
                    <UiButton variant="ghost" size="sm" type="button" onClick={() => setEditing(g)}>
                      Изменить
                    </UiButton>
                  )}
                  {canDelete && (
                    <UiButton
                      variant="danger"
                      size="sm"
                      type="button"
                      onClick={() => {
                        if (window.confirm('Отвязать товар от филиала?')) {
                          dispatch(goodsThunks.remove({ branchId, id: g.id }));
                        }
                      }}
                    >
                      Удалить
                    </UiButton>
                  )}
                </td>
              </tr>
            ))}
            {!list.length && listStatus !== 'loading' && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                  Пока нет товаров
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GoodsForm({ branchId, goods, saveStatus, saveError, onCancel, onSaved }) {
  const dispatch = useDispatch();
  const isNew = !goods.id;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: goods.name ?? '',
      articleNumber: goods.articleNumber ?? '',
      description: goods.description ?? '',
      purchasePrice: goods.purchasePrice ?? '',
      defaultPrice: goods.defaultPrice ?? '',
      price: goods.price ?? '',
      goodsCount: goods.goodsCount ?? 0,
    },
  });

  const onSubmit = async (values) => {
    const toNum = (v) => (v === '' ? null : Number(v));
    const payload = {
      name: values.name.trim(),
      articleNumber: values.articleNumber.trim() || null,
      description: values.description.trim() || null,
      purchasePrice: toNum(values.purchasePrice),
      defaultPrice: toNum(values.defaultPrice),
      price: toNum(values.price),
      goodsCount: Number(values.goodsCount) || 0,
    };
    const action = isNew
      ? goodsThunks.create({ branchId, payload })
      : goodsThunks.update({ branchId, id: goods.id, payload });
    const res = await dispatch(action);
    if (res.meta.requestStatus === 'fulfilled') onSaved();
  };

  return (
    <form className="form-card" onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 16 }}>
      <h3>{isNew ? 'Новый товар' : 'Редактирование'}</h3>
      <div className="form-grid">
        <div className="field">
          <label>Название</label>
          <UiInput {...register('name', { required: 'Обязательно' })} />
          {errors.name && <span className="form-error">{errors.name.message}</span>}
        </div>
        <div className="field">
          <label>Артикул</label>
          <UiInput {...register('articleNumber')} />
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Описание</label>
          <UiTextarea rows={2} {...register('description')} />
        </div>
        <div className="field">
          <label>Закупочная цена</label>
          <UiInput type="number" step="0.01" {...register('purchasePrice')} />
        </div>
        <div className="field">
          <label>Цена по умолчанию</label>
          <UiInput type="number" step="0.01" {...register('defaultPrice')} />
        </div>
        <div className="field">
          <label>Цена (филиал)</label>
          <UiInput type="number" step="0.01" {...register('price')} />
        </div>
        <div className="field">
          <label>Остаток (филиал)</label>
          <UiInput type="number" {...register('goodsCount')} />
        </div>
      </div>

      {saveError && <div className="form-error">{saveError}</div>}

      <div className="form-actions">
        <UiButton type="submit" disabled={saveStatus === 'loading'}>
          {saveStatus === 'loading' ? 'Сохранение…' : 'Сохранить'}
        </UiButton>
        <UiButton variant="ghost" type="button" onClick={onCancel}>
          Отмена
        </UiButton>
      </div>
    </form>
  );
}
